/**
 * MeshPacket — Bitchat-compatible binary packet format
 *
 * Header layout (little-endian, 13 fixed bytes):
 *   0      1   version (currently 1)
 *   1      1   type
 *   2      1   ttl
 *   3      8   timestamp (BigUint64 ms)
 *  11      1   flags
 *  12      2   payloadLength (Uint16 BE, Bitchat convention)
 *
 * Followed by:
 *   senderId    8 bytes (always present)
 *   recipientId 8 bytes (if flag HAS_RECIPIENT set; 0xFF*8 means broadcast)
 *   payload     N bytes (length given in header)
 *   signature  64 bytes (if flag HAS_SIGNATURE set)
 *
 * This slice implements: Announce, Broadcast and Ack packets without
 * signatures or Noise encryption. The Ed25519 signing path is left as a
 * stub so follow-up slices can add it without header churn.
 */

export const VERSION = 1;

// Message types — values chosen to match the real Bitchat protocol so that
// our WebBluetoothTransport can interoperate with the iOS/Android Bitchat
// apps. Source: localPackages/BitFoundation/Sources/BitFoundation/MessageType.swift
export const TYPE = Object.freeze({
  ANNOUNCE:        0x01,  // peer advertises identity (nickname + public keys, TLV-encoded)
  MESSAGE:         0x02,  // plaintext message; broadcast if no recipient, private otherwise
  LEAVE:           0x03,  // peer politely leaves the mesh
  NOISE_HANDSHAKE: 0x10,  // Noise XX handshake init/resp (deferred: we don't speak Noise yet)
  NOISE_ENCRYPTED: 0x11,  // encrypted DM (deferred)
  FRAGMENT:        0x20,  // reassembly fragment (see Fragmentation.js)
});

export const FLAGS = Object.freeze({
  NONE:            0,
  HAS_RECIPIENT:   1 << 0,
  HAS_SIGNATURE:   1 << 1,
  IS_COMPRESSED:   1 << 2,
});

const HEADER_SIZE      = 14;
const PEER_ID_SIZE     = 8;
const SIGNATURE_SIZE   = 64;
const BROADCAST_ID     = new Uint8Array(PEER_ID_SIZE).fill(0xFF);

const encoder = new TextEncoder();
const decoder = new TextDecoder('utf-8', { fatal: false });

// ---------- helpers ----------

function writeBigUint64BE(view, offset, value) {
  // Split into high / low 32-bit halves to stay DataView-compatible
  const hi = Math.floor(value / 0x100000000);
  const lo = value >>> 0;
  view.setUint32(offset, hi, false);
  view.setUint32(offset + 4, lo, false);
}
function readBigUint64BE(view, offset) {
  const hi = view.getUint32(offset, false);
  const lo = view.getUint32(offset + 4, false);
  return hi * 0x100000000 + lo;
}

function bytesEqual(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

// ---------- public API ----------

/**
 * Compute the canonical 16-byte packet identifier used for gossip dedup.
 * Derived from senderId + timestamp + first 2 bytes of payload — cheap and
 * uniquely identifies a packet for any realistic collision window.
 */
export function packetIdOf(pkt) {
  const id = new Uint8Array(16);
  id.set(pkt.senderId, 0);
  const view = new DataView(id.buffer);
  writeBigUint64BE(view, 8, Number(pkt.timestamp));
  if (pkt.payload && pkt.payload.length >= 2) {
    // XOR a couple of payload bytes into the last word so two packets from
    // the same sender at the same millisecond still differ.
    id[14] ^= pkt.payload[0];
    id[15] ^= pkt.payload[1];
  }
  return id;
}

/**
 * Encode a packet object to its wire form.
 *
 * @param {{
 *   version?: number,
 *   type: number,
 *   ttl?: number,
 *   timestamp?: number,
 *   flags?: number,
 *   senderId: Uint8Array,
 *   recipientId?: Uint8Array,
 *   payload?: Uint8Array | string,
 *   signature?: Uint8Array,
 * }} pkt
 * @returns {Uint8Array}
 */
export function encode(pkt) {
  const version   = pkt.version ?? VERSION;
  const ttl       = pkt.ttl ?? 7;
  const timestamp = pkt.timestamp ?? Date.now();

  let payload = pkt.payload ?? new Uint8Array(0);
  if (typeof payload === 'string') payload = encoder.encode(payload);

  // Authoritatively recompute the auto-managed flag bits so callers can
  // zero out pkt.recipientId / pkt.signature and get a flags byte that
  // reflects the actual bytes in the packet (important for signable-form
  // construction, where we spread an existing packet and null the signature).
  let flags = (pkt.flags ?? FLAGS.NONE)
              & ~(FLAGS.HAS_RECIPIENT | FLAGS.HAS_SIGNATURE);
  if (pkt.recipientId) flags |= FLAGS.HAS_RECIPIENT;
  if (pkt.signature)   flags |= FLAGS.HAS_SIGNATURE;

  const recipientLen = (flags & FLAGS.HAS_RECIPIENT) ? PEER_ID_SIZE : 0;
  const signatureLen = (flags & FLAGS.HAS_SIGNATURE) ? SIGNATURE_SIZE : 0;
  const total = HEADER_SIZE + PEER_ID_SIZE + recipientLen + payload.length + signatureLen;

  const buf = new Uint8Array(total);
  const view = new DataView(buf.buffer);
  let o = 0;
  buf[o++] = version;
  buf[o++] = pkt.type;
  buf[o++] = ttl;
  writeBigUint64BE(view, o, timestamp); o += 8;
  buf[o++] = flags;
  view.setUint16(o, payload.length, false); o += 2;

  buf.set(pkt.senderId, o); o += PEER_ID_SIZE;
  if (flags & FLAGS.HAS_RECIPIENT) { buf.set(pkt.recipientId, o); o += PEER_ID_SIZE; }
  buf.set(payload, o); o += payload.length;
  if (flags & FLAGS.HAS_SIGNATURE) { buf.set(pkt.signature, o); o += SIGNATURE_SIZE; }

  return buf;
}

/**
 * Decode a wire-form buffer back to a packet object.
 */
export function decode(bytes) {
  if (bytes.length < HEADER_SIZE + PEER_ID_SIZE) {
    throw new Error(`MeshPacket: buffer too small (${bytes.length})`);
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let o = 0;
  const version       = bytes[o++];
  const type          = bytes[o++];
  const ttl           = bytes[o++];
  const timestamp     = readBigUint64BE(view, o); o += 8;
  const flags         = bytes[o++];
  const payloadLength = view.getUint16(o, false); o += 2;

  const senderId = bytes.slice(o, o + PEER_ID_SIZE); o += PEER_ID_SIZE;

  let recipientId = null;
  if (flags & FLAGS.HAS_RECIPIENT) {
    recipientId = bytes.slice(o, o + PEER_ID_SIZE);
    o += PEER_ID_SIZE;
  }

  const payload = bytes.slice(o, o + payloadLength); o += payloadLength;

  let signature = null;
  if (flags & FLAGS.HAS_SIGNATURE) {
    signature = bytes.slice(o, o + SIGNATURE_SIZE);
    o += SIGNATURE_SIZE;
  }

  return { version, type, ttl, timestamp, flags, senderId, recipientId, payload, signature };
}

/**
 * Utility: decode payload bytes as UTF-8 (best effort).
 */
export function payloadToString(payload) {
  return decoder.decode(payload);
}

/**
 * Utility: is a decoded packet addressed to the broadcast ID?
 */
export function isBroadcastTarget(recipientId) {
  return !recipientId || bytesEqual(recipientId, BROADCAST_ID);
}

/**
 * Utility: produce a new packet identical to the input but with TTL - 1.
 * Used by the relay path. Returns null if TTL would drop to zero.
 */
export function decrementTtl(pkt) {
  if (pkt.ttl <= 1) return null;
  return { ...pkt, ttl: pkt.ttl - 1 };
}

export const BROADCAST_RECIPIENT = BROADCAST_ID;

// ---------- PKCS#7-style padding (iOS MessagePadding.swift) ----------
//
// iOS Bitchat pads packets to one of [256, 512, 1024, 2048] byte blocks,
// choosing the smallest block >= (dataSize + 16). Padding bytes all carry
// the same value == paddingNeeded. If paddingNeeded > 255, padding is
// skipped (the packet goes out unpadded). The padded form is what iOS
// signs AND verifies against, so we must apply identical padding to our
// signable bytes or signature verification will fail.
//
// iOS's decoder accepts both padded and unpadded wire forms (it tries raw
// first, then unpadded), so sending unpadded on the wire is fine; only the
// SIGNABLE bytes strictly require padding for interop.

const BLOCK_SIZES = [256, 512, 1024, 2048];

/** Choose the target block size per iOS MessagePadding.optimalBlockSize. */
export function optimalBlockSize(dataSize) {
  const totalSize = dataSize + 16;
  for (const b of BLOCK_SIZES) {
    if (totalSize <= b) return b;
  }
  return dataSize; // larger than 2048: unpadded, caller is expected to fragment
}

/** Append PKCS#7 padding up to targetSize. Returns unchanged if no-op. */
export function pad(bytes, targetSize) {
  if (bytes.length >= targetSize) return bytes;
  const paddingNeeded = targetSize - bytes.length;
  if (paddingNeeded <= 0 || paddingNeeded > 255) return bytes;
  const out = new Uint8Array(targetSize);
  out.set(bytes, 0);
  out.fill(paddingNeeded, bytes.length);
  return out;
}

/** Encode + iOS-compatible padding. Use for signing AND for wire writes. */
export function encodePadded(pkt) {
  const raw = encode(pkt);
  return pad(raw, optimalBlockSize(raw.length));
}

export default {
  VERSION, TYPE, FLAGS,
  encode, decode, packetIdOf, payloadToString, isBroadcastTarget, decrementTtl,
  pad, optimalBlockSize, encodePadded,
  BROADCAST_RECIPIENT,
};
